import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CampaignIdea, CampaignIdeasOverview, CampaignOccasion, createMarketingEvent, deleteMarketingEvent, dismissCampaignIdea,
  draftCampaignIdea, generateCampaignIdeas, getCampaignIdeas
} from '../../../api/services/marketingService';
import { ago } from './shared';
import { BigButton, Card, CardHeader, FIELD, LinkButton, StatusPill } from '../../../components/ui/dg';

/**
 * Idées de campagnes (`?idees=1`). L'API calcule le calendrier des semaines à
 * venir (fériés, fêtes religieuses estimées, saisons, événements de
 * l'équipe) ; l'IA propose des idées sur ces dates et rédige celle qu'on
 * choisit. La campagne rédigée suit ensuite le circuit habituel.
 */
interface IdeasProps {
  token: string;
  onBack: () => void;
  onOpenCampaign: (ref: string) => void;
}

const AUDIENCE: Record<string, string> = { all: 'Tout le monde', renters: 'Locataires', owners: 'Propriétaires' };
const OCCASION_DOT: Record<CampaignOccasion['kind'], string> = { holiday: 'bg-[#F59E0B]', religious: 'bg-[#F59E0B]', event: 'bg-[#4F46E5]', season: 'bg-[#10B981]' };

const parseDay = (day: string) => new Date(`${day}T12:00:00Z`);
const dayMonth = (day: string) => parseDay(day).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const longDay = (day: string) => parseDay(day).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
const span = (from: string, to?: string | null) => (!to || to === from ? longDay(from) : `du ${dayMonth(from)} au ${dayMonth(to)}`);
const inDays = (today: string, day: string) => Math.round((parseDay(day).getTime() - parseDay(today).getTime()) / 86400000);
const when = (today: string, day: string) => {
  const n = inDays(today, day);
  return n <= 0 ? 'aujourd’hui' : n === 1 ? 'demain' : `dans ${n} jours`;
};

/** Message lisible pour une erreur du fournisseur d'IA (même idée que sur la page campagne). */
const aiErrorText = (message: string) => {
  if (/429|rate limit/i.test(message)) return 'L’IA est très sollicitée en ce moment, réessayez dans une minute.';
  if (/401|403|unauthorized|tier/i.test(message)) return 'La clé d’IA est refusée par le fournisseur. Vérifiez-la dans Coolify.';
  if (/timeout|abort/i.test(message)) return 'L’IA a mis trop de temps à répondre, réessayez.';
  return message;
};

const DateTile = ({ day }: { day: string }) => {
  const d = parseDay(day);
  return (
    <div className="flex h-[52px] w-[52px] shrink-0 flex-col items-center justify-center rounded-2xl bg-[#EEEDFD] text-[#4F46E5]">
      <span className="text-[18px] font-extrabold leading-none">{d.getUTCDate()}</span>
      <span className="mt-0.5 text-[10.5px] font-bold uppercase">{d.toLocaleDateString('fr-FR', { month: 'short', timeZone: 'UTC' }).replace('.', '')}</span>
    </div>
  );
};

const IdeaCard = ({ idea, today, busy, locked, onDraft, onDismiss, onOpen }: {
  idea: CampaignIdea; today: string; busy: 'draft' | 'dismiss' | null; locked: boolean;
  onDraft: () => void; onDismiss: () => void; onOpen: () => void;
}) => {
  const drafted = idea.status === 'drafted';
  return (
    <li className={`flex gap-4 px-5 py-4 lg:px-7 ${drafted ? 'bg-[#FAFAFD]' : ''}`}>
      {idea.send_on ? <DateTile day={idea.send_on} /> : <div className="h-[52px] w-[52px] shrink-0 rounded-2xl bg-[#F1F2F8]" />}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusPill tone="grey">{AUDIENCE[idea.audience] || idea.audience}</StatusPill>
          {idea.occasion_label && <StatusPill tone="amber">{idea.occasion_label}{idea.occasion_date ? `, ${dayMonth(idea.occasion_date)}` : ''}</StatusPill>}
          {idea.send_on && <span className="text-[12px] text-[#6B7280]">Envoi conseillé {when(today, idea.send_on)}</span>}
        </div>
        <h4 className="mt-1.5 text-[15px] font-bold leading-snug text-[#1E293B]">{idea.title}</h4>
        {idea.angle && <p className="mt-1 text-[13.5px] leading-[1.5] text-[#334155]">{idea.angle}</p>}
        {idea.why && <p className="mt-1 text-[12.5px] leading-[1.5] text-[#6B7280]">{idea.why}</p>}
        {idea.draft_error && !idea.drafting && <p className="mt-2 rounded-lg bg-[#FDECEF] px-2.5 py-1.5 text-[12.5px] text-[#B91C1C]">La rédaction a échoué. {aiErrorText(idea.draft_error)}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-4">
          {drafted ? (
            <>
              <span className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#047857]">
                <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M3 8.5l3 3 7-7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                Brouillon {idea.campaign_number ? `!${idea.campaign_number}` : ''} rédigé
              </span>
              <LinkButton onClick={onOpen}>Ouvrir la campagne</LinkButton>
            </>
          ) : (
            <>
              <button type="button" disabled={locked} onClick={onDraft}
                className="inline-flex h-9 items-center gap-2 rounded-2xl bg-[#4F46E5] px-4 text-[13px] font-bold text-white hover:bg-[#4338CA] disabled:opacity-50">
                {busy === 'draft' && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                {busy === 'draft' ? 'Rédaction en fr, en, ar…' : idea.draft_error ? 'Réessayer' : 'Rédiger la campagne'}
              </button>
              <button type="button" disabled={locked} onClick={onDismiss}
                className="text-[13px] font-semibold text-[#6B7280] hover:text-[#1E293B] disabled:opacity-40">
                {busy === 'dismiss' ? 'Mise à l’écart…' : 'Écarter'}
              </button>
            </>
          )}
        </div>
      </div>
    </li>
  );
};

const EventForm = ({ token, onAdded }: { token: string; onAdded: () => void }) => {
  const [title, setTitle] = useState('');
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [place, setPlace] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !startsOn) return;
    setSaving(true);
    setError(null);
    try {
      await createMarketingEvent(token, { title: title.trim(), starts_on: startsOn, ends_on: endsOn || null, place: place.trim() || null });
      setTitle(''); setStartsOn(''); setEndsOn(''); setPlace('');
      onAdded();
    } catch (err: any) {
      setError(err.message || 'Ajout impossible.');
    } finally {
      setSaving(false);
    }
  };

  const small = FIELD.replace('h-[52px]', 'h-10').replace('text-[15px]', 'text-[13.5px]').replace('px-4', 'px-3').replace('rounded-2xl', 'rounded-xl');
  return (
    <form onSubmit={submit} className="space-y-2 px-5 pb-5 pt-2">
      <input className={small} placeholder="Festival, match, salon…" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[11.5px] font-semibold text-[#6B7280]">Début
          <input type="date" className={`${small} mt-1`} value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
        </label>
        <label className="text-[11.5px] font-semibold text-[#6B7280]">Fin (facultatif)
          <input type="date" className={`${small} mt-1`} value={endsOn} min={startsOn || undefined} onChange={(e) => setEndsOn(e.target.value)} />
        </label>
      </div>
      <input className={small} placeholder="Lieu (facultatif)" value={place} onChange={(e) => setPlace(e.target.value)} maxLength={120} />
      {error && <p className="text-[12.5px] text-[#B91C1C]">{error}</p>}
      <button type="submit" disabled={saving || !title.trim() || !startsOn}
        className="h-9 w-full rounded-xl bg-[#F1F2F8] text-[13px] font-bold text-[#1E293B] hover:bg-[#E7E9F2] disabled:opacity-50">
        {saving ? 'Ajout…' : 'Ajouter au calendrier'}
      </button>
    </form>
  );
};

const CampaignIdeas = ({ token, onBack, onOpenCampaign }: IdeasProps) => {
  const [data, setData] = useState<CampaignIdeasOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [busy, setBusy] = useState<{ id: string; what: 'dismiss' } | null>(null);
  // Idée dont on attend le brouillon, pour l'ouvrir dès qu'il est prêt.
  const awaitedDraft = useRef<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await getCampaignIdeas(token));
    } catch (err: any) {
      setError(err.message || 'Idées indisponibles.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // La génération et la rédaction tournent sur l'API (un appel à l'IA peut
  // durer plus d'une minute) : on relit l'état toutes les 3 secondes.
  const generationRunning = Boolean(data?.generation?.running);
  const anyDrafting = Boolean(data?.ideas.some((i) => i.drafting));
  useEffect(() => {
    if (!generationRunning && !anyDrafting) return undefined;
    const timer = window.setTimeout(load, 3000);
    return () => window.clearTimeout(timer);
  }, [data, generationRunning, anyDrafting, load]);

  useEffect(() => {
    const id = awaitedDraft.current;
    const idea = id ? data?.ideas.find((i) => i.id_idea === id) : null;
    if (!idea) return;
    if (idea.status === 'drafted') {
      awaitedDraft.current = null;
      onOpenCampaign(String(idea.campaign_number || idea.id_campaign));
    } else if (!idea.drafting) {
      awaitedDraft.current = null;
    }
  }, [data, onOpenCampaign]);

  const generating = starting || generationRunning;

  const generate = async () => {
    setStarting(true);
    setError(null);
    try {
      setData(await generateCampaignIdeas(token));
    } catch (err: any) {
      setError(aiErrorText(err.message || 'Génération impossible.'));
    } finally {
      setStarting(false);
    }
  };

  const draft = async (idea: CampaignIdea) => {
    setError(null);
    awaitedDraft.current = idea.id_idea;
    setData((d) => (d ? { ...d, ideas: d.ideas.map((i) => (i.id_idea === idea.id_idea ? { ...i, drafting: true, draft_error: null } : i)) } : d));
    try {
      setData(await draftCampaignIdea(token, idea.id_idea));
    } catch (err: any) {
      awaitedDraft.current = null;
      setError(aiErrorText(err.message || 'Rédaction impossible.'));
      load();
    }
  };

  // Échec de la dernière génération, s'il est récent.
  const generation = data?.generation;
  const generationError = generation && !generation.running && generation.error && generation.finished_at
    && Date.now() - new Date(generation.finished_at).getTime() < 15 * 60 * 1000 ? aiErrorText(generation.error) : null;

  const dismiss = async (idea: CampaignIdea) => {
    setBusy({ id: idea.id_idea, what: 'dismiss' });
    try {
      await dismissCampaignIdea(token, idea.id_idea);
      setData((d) => (d ? { ...d, ideas: d.ideas.filter((i) => i.id_idea !== idea.id_idea) } : d));
    } catch (err: any) {
      setError(err.message || 'Impossible d’écarter cette idée.');
    } finally {
      setBusy(null);
    }
  };

  const removeEvent = async (id: string) => {
    try {
      await deleteMarketingEvent(token, id);
      load();
    } catch (err: any) {
      setError(err.message || 'Impossible de retirer cet événement.');
    }
  };

  const ideas = data?.ideas || [];
  const fresh = ideas.filter((i) => i.status === 'new');
  const drafted = ideas.filter((i) => i.status === 'drafted');
  const today = data?.today || new Date().toISOString().slice(0, 10);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#EEF0F4] px-5 pb-5 pt-5 lg:px-7">
          <div className="min-w-0">
            <button type="button" onClick={onBack} className="text-[12.5px] font-semibold text-[#6B7280] hover:text-[#1E293B]">Campagnes</button>
            <h2 className="mt-1 text-[20px] font-extrabold text-[#1E293B]">Idées de campagnes</h2>
            <p className="mt-1 max-w-[560px] text-[13px] leading-[1.5] text-[#6B7280]">
              L’IA part du calendrier des {data ? Math.round(data.horizon_days / 7) : 5} prochaines semaines, des campagnes récentes et de l’activité de l’app.
              Les dates viennent du calendrier, jamais de l’IA. Aucune récompense n’est proposée.
            </p>
          </div>
          <BigButton onClick={generate} disabled={generating || !data?.configured} className="h-11 shrink-0 px-5 text-[14px]">
            {generating && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
            {generating ? 'L’IA réfléchit…' : fresh.length > 0 ? 'Nouvelles idées' : 'Proposer des idées'}
          </BigButton>
        </div>

        {(error || generationError) && <p className="mx-5 mt-4 rounded-xl bg-[#FDECEF] px-3 py-2 text-[13px] text-[#B91C1C] lg:mx-7">{error || `La génération a échoué. ${generationError}`}</p>}
        {data && !data.configured && (
          <p className="mx-5 mt-4 rounded-xl bg-[#FFF8EB] px-3 py-2 text-[13px] text-[#92400E] lg:mx-7">Aucune clé d’IA n’est configurée sur l’API (GEMINI_API_KEY ou MISTRAL_API_KEY dans Coolify).</p>
        )}

        {loading ? (
          <p className="px-7 py-10 text-center text-[13.5px] text-[#6B7280]">Chargement…</p>
        ) : fresh.length === 0 && drafted.length === 0 ? (
          <div className="px-7 py-12 text-center">
            <p className="text-[14px] font-semibold text-[#1E293B]">Pas d’idée en attente</p>
            <p className="mx-auto mt-1 max-w-[380px] text-[13px] text-[#6B7280]">
              {generating ? 'Quelques secondes, le temps de parcourir le calendrier.' : 'Demandez-en à l’IA, ou attendez lundi matin : elle en propose chaque semaine dans le groupe Telegram.'}
            </p>
          </div>
        ) : (
          <>
            <ul className={`divide-y divide-[#EEF0F4] ${generating ? 'opacity-50' : ''}`}>
              {fresh.map((idea) => (
                <IdeaCard key={idea.id_idea} idea={idea} today={today}
                  busy={idea.drafting ? 'draft' : busy?.id === idea.id_idea ? busy.what : null} locked={busy !== null || generating || anyDrafting}
                  onDraft={() => draft(idea)} onDismiss={() => dismiss(idea)} onOpen={() => {}} />
              ))}
            </ul>
            {drafted.length > 0 && (
              <>
                <div className="border-t border-[#EEF0F4] px-5 pb-1 pt-4 lg:px-7"><span className="text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]">Déjà rédigées</span></div>
                <ul className="divide-y divide-[#EEF0F4]">
                  {drafted.map((idea) => (
                    <IdeaCard key={idea.id_idea} idea={idea} today={today} busy={null} locked={false} onDraft={() => {}} onDismiss={() => {}}
                      onOpen={() => onOpenCampaign(String(idea.campaign_number || idea.id_campaign))} />
                  ))}
                </ul>
              </>
            )}
            {data?.generated_at && (
              <p className="border-t border-[#EEF0F4] px-5 py-3 text-[12px] text-[#9CA3AF] lg:px-7">
                Proposées {ago(data.generated_at)}{fresh[0]?.model ? ` par ${fresh[0].model}` : ''}. Une idée écartée n’est plus reproposée.
              </p>
            )}
          </>
        )}
      </Card>

      <div className="space-y-6 lg:sticky lg:top-4 lg:self-start">
        <Card>
          <CardHeader title="Calendrier" />
          {!data || data.calendar.length === 0 ? (
            <p className="px-5 pb-5 text-[13px] text-[#6B7280]">{loading ? 'Chargement…' : 'Rien de particulier dans les semaines à venir.'}</p>
          ) : (
            <ul className="px-5 pb-4">
              {data.calendar.map((o) => (
                <li key={`${o.key}-${o.starts_on}`} className="flex items-start gap-3 py-2">
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${OCCASION_DOT[o.kind] || 'bg-[#9CA3AF]'}`} />
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-semibold leading-snug text-[#1E293B]">{o.label}</p>
                    <p className="text-[12px] text-[#6B7280]">
                      {span(o.starts_on, o.ends_on)}
                      {o.estimated && <span className="ml-1.5 text-[#B45309]">· estimée</span>}
                    </p>
                    {o.note && !o.note.startsWith('date') && <p className="text-[12px] text-[#9CA3AF]">{o.note}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader title="Événements de l’équipe" />
          <p className="px-5 text-[12.5px] leading-[1.5] text-[#6B7280]">Ce que le code ne peut pas deviner, comme un festival, un match ou un salon. L’IA en tient compte à la prochaine génération.</p>
          {data && data.events.length > 0 && (
            <ul className="mt-2 px-5">
              {data.events.map((e) => (
                <li key={e.id_event} className="group flex items-start justify-between gap-2 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-[13.5px] font-semibold text-[#1E293B]">{e.title}</p>
                    <p className="text-[12px] text-[#6B7280]">{span(e.starts_on, e.ends_on)}{e.place ? `, ${e.place}` : ''}</p>
                  </div>
                  <button type="button" onClick={() => removeEvent(e.id_event)} aria-label={`Retirer ${e.title}`}
                    className="mt-0.5 rounded-lg px-1.5 text-[16px] leading-none text-[#9CA3AF] opacity-60 hover:bg-[#F1F2F8] hover:text-[#B91C1C] group-hover:opacity-100">×</button>
                </li>
              ))}
            </ul>
          )}
          <EventForm token={token} onAdded={load} />
        </Card>
      </div>
    </div>
  );
};

export default CampaignIdeas;
